import { PipelineConfig, SourceRef, TaskKind, TEAM_TAGS } from './types';

export type RepoRef = { owner: string; repo: string };

/**
 * 파이프라인 디렉터리 안의 파일들. 가이드의 「단계 1」 구조다.
 *
 * 이전 모델은 `dagFilePath` 하나였다. 새 규약은 **디렉터리 하나에 3 개 이상**
 * 이고 세 파일이 이름으로 서로를 찾으므로, 개별 경로를 따로 계산하게 두면
 * 어긋날 수 있다. 하나의 함수가 한 번에 만든다.
 */
export type PipelineFiles = {
  /** `dags-pa/tech/product_metric/daily_order_summary` */
  dir: string;
  /** `{dir}/PA_dag_daily_order_summary.py` */
  pyPath: string;
  /** `{dir}/conf/PA_dag_daily_order_summary.yaml` */
  confPath: string;
  /** 테이블 그룹마다 하나. 「넣기」 가 켜진 그룹만 들어간다. */
  sqlPaths: { tableId: string; path: string; include: string }[];
  /** 위 세 종류 전부. 삭제·sha 비교가 쓴다. */
  allPaths: string[];
};

/** `dags-pa`. 저장소 루트의 팀 디렉터리. */
export function teamDir(teamTag: string): string {
  return `dags-${teamTag.toLowerCase()}`;
}

/**
 * 폴더 경로를 정규화한다. 앞뒤 슬래시와 빈 조각을 걷어내 `a/b` 형태만 남긴다.
 *
 * 팀마다 단계 수가 다르다(실측: `dags-mds/metric_review` 1 단 ~
 * `dags-mg/sales/mga_mart/vendor/routine` 4 단) 므로 단계 수를 제한하지 않는다.
 */
export function normalizeFolder(folder: string | undefined): string {
  return (folder ?? '')
    .split('/')
    .map(s => s.trim())
    .filter(Boolean)
    .join('/');
}

/**
 * `{TEAM}_dag_{이름}` — Airflow `dag_id` 이자 Backstage 엔티티 이름이자 파일 stem.
 *
 * 식별자를 하나로 합친 이유: 이전에는 엔티티 이름(`pipeline_name`)과 `dag_id` 와
 * 파일 stem 이 서로 다른 규약을 갖고 있었고, 어느 쪽이 권위인지 코드마다 달랐다.
 */
export function buildDagId(opts: { teamTag: string; name: string }): string {
  return `${opts.teamTag}_dag_${opts.name}`;
}

/** Jira 이슈 주소. */
export function ticketUrl(ticketNumber: string): string {
  return `https://rgpkorea.atlassian.net/browse/${ticketNumber}`;
}

/**
 * 설명이 비어 있으면 그 PR 의 티켓 링크로 채운다.
 *
 * 설명은 폼에서 선택값이지만 conf yaml 의 `description` 은 비면 안 된다 —
 * dp-lakehouse `tests/dags/test_dag_integrity.py` 의
 * `test_all_dags_have_description` 이 `dags-dp` DAG 의 빈 설명을 실패시킨다
 * (다른 팀 디렉터리는 DagBag 이 읽지 않아 통과한다). 티켓 번호는 브랜치 이름이라
 * 늘 있으므로 대체값으로 쓴다.
 *
 * 미리보기·생성·수정이 **같은 결과**를 내도록 프론트(미리보기·제출)와 백엔드
 * (렌더 직전)가 모두 이 함수를 거친다. 수정 때 비우면 그 수정 티켓의 링크가 된다.
 */
export function withDescriptionFallback(
  config: PipelineConfig,
  ticketNumber: string,
): PipelineConfig {
  if (config.description.trim()) return config;
  return { ...config, description: ticketUrl(ticketNumber) };
}

/** `dag_id` 의 `{TEAM}_dag_` 접두사를 뗀 부분. 접두사 불일치면 throw 한다. */
export function dagShortName(dagId: string, teamTag: string): string {
  const prefix = `${teamTag}_dag_`;
  if (!dagId.startsWith(prefix)) {
    throw new Error(
      `dag_id(${dagId}) 가 ${prefix} 로 시작하지 않습니다 — team_tag 가 맞는지 확인하세요.`,
    );
  }
  return dagId.slice(prefix.length);
}

/**
 * `{TEAM}_task_{kind}_{table_id}` — conf yaml 의 키이자 `task_id`.
 *
 * `.py` 의 `tasks["..."]` 조회 키와 **완전히 같아야** 한다. 다르면 실행 시
 * `KeyError` 로 죽는다. 양쪽을 이 함수 하나로 만든다.
 *
 * 이름에 `table_id` 가 들어가는 이유: 테이블 그룹이 여러 개면 kind 만으로는
 * 겹친다. 실제 레포도 테이블 이름을 쓴다(`AN_task_bq_del_smr_daily_ypx_index_daily`).
 */
export function buildTaskName(opts: {
  teamTag: string;
  kind: TaskKind;
  tableId: string;
}): string {
  return `${opts.teamTag}_task_${opts.kind}_${opts.tableId}`;
}

/** `.py` 안의 파이썬 변수명. `var_task_delete_{table}` / `task_delete_{table}`. */
export function taskVarNames(
  kind: TaskKind,
  tableId: string,
): { conf: string; operator: string } {
  return {
    conf: `var_task_${kind}_${tableId}`,
    operator: `task_${kind}_${tableId}`,
  };
}

/** 경로 규칙의 단일 구현. */
export function pipelineFiles(opts: {
  teamTag: string;
  dagFolder: string;
  dagId: string;
  /** 「넣기」 가 켜진 그룹의 테이블 이름들. 순서를 유지한다. */
  insertTableIds: string[];
}): PipelineFiles {
  const { teamTag, dagId, insertTableIds } = opts;
  const folder = normalizeFolder(opts.dagFolder);
  const dir = [teamDir(teamTag), folder].filter(Boolean).join('/');
  const pyPath = `${dir}/${dagId}.py`;
  const confPath = `${dir}/conf/${dagId}.yaml`;
  const sqlPaths = insertTableIds.map(tableId => ({
    tableId,
    path: `${dir}/sql/${tableId}.sql`,
    // `template_searchpath` 에 `sql/` 가 들어 있어 include 는 파일명만 쓴다.
    include: `${tableId}.sql`,
  }));
  return {
    dir,
    pyPath,
    confPath,
    sqlPaths,
    allPaths: [pyPath, confPath, ...sqlPaths.map(s => s.path)],
  };
}

/** catalog repo 쪽 경로. 엔티티 이름이 `dag_id` 이므로 그것을 폴더로 쓴다. */
export function catalogFiles(opts: { teamTag: string; dagId: string }): {
  dir: string;
  infoPath: string;
} {
  const dir = `catalog/${opts.teamTag}/${opts.dagId}`;
  return { dir, infoPath: `${dir}/catalog-info.yaml` };
}

/** `backstage.io/source-location` annotation 값. 디렉터리를 가리킨다. */
export function buildSourceLocation(opts: {
  dagRepo: RepoRef;
  branch: string;
  dir: string;
}): string {
  const { dagRepo, branch, dir } = opts;
  return `url:https://github.com/${dagRepo.owner}/${dagRepo.repo}/tree/${branch}/${dir}`;
}

/**
 * 테이블 이름을 아직 입력하지 않았을 때 SQL 머리말에 들어가는 자리표시자.
 *
 * **매크로가 아니다.** `user_defined_macros` 는 DAG 단위 하나뿐이라 테이블
 * 그룹이 2 개 이상이면 `{{ TABLE_NAME }}` 이 어느 테이블인지 정할 수 없다
 * (실제 레포의 다중 테이블 DAG 도 테이블만 직접 적는다). 폼이 이름을 받는
 * 순간 이 자리에 실제 이름을 채워 넣고, 남아 있으면 검증이 거부한다 —
 * 비워 두면 `` `{{ PROJECT_ID }}.{{ DATA_SET }}.` `` 처럼 점으로 끝나 무엇을
 * 채워야 하는지 알 수 없다.
 */
export const TABLE_NAME_PLACEHOLDER = 'TABLE_NAME';

/**
 * 쿼리·SQL 이 참조하는 대상 테이블 식. 프로젝트·데이터셋만 매크로다 —
 * 테이블은 그룹마다 다르므로 `user_defined_macros` 로 표현할 수 없고,
 * 실제 레포의 다중 테이블 DAG 도 테이블만 직접 적는다.
 */
export function buildTargetRef(tableId: string): string {
  return `\`{{ PROJECT_ID }}.{{ DATA_SET }}.${
    tableId || TABLE_NAME_PLACEHOLDER
  }\``;
}

/**
 * SQL 의 `FROM` 에 들어가는 원천 테이블 식 — 백틱 포함 `` `p.d.t` ``.
 *
 * 비어 있는 조각은 `{프로젝트}` 처럼 자리표시로 둔다. 폼이 조각을 하나씩 채울 때마다
 * `replaceSourceRef` 가 직전 식을 찾아 바꾸므로, 자리표시도 **찾을 수 있는 고정된
 * 문자열**이어야 한다. 단일 중괄호라 Jinja 가 해석하지 않는다.
 */
export function buildSourceRef(source: SourceRef | undefined): string {
  const p = source?.project_id || '{프로젝트}';
  const d = source?.data_set || '{데이터셋}';
  const t = source?.table_id || '{테이블}';
  return `\`${p}.${d}.${t}\``;
}

/**
 * 원천이 바뀌면 SQL 안의 직전 원천 식을 새 식으로 바꾼다.
 *
 * 테이블 이름처럼 「SQL 이 기본 뼈대와 같을 때만 다시 만든다」 로 하지 않는 이유:
 * 원천은 SELECT 를 다 쓴 뒤에 고치는 일이 흔한데, 그때는 SQL 이 이미 뼈대와 다르다.
 * 식 문자열만 바꾸면 사용자가 쓴 나머지 SQL 은 건드리지 않는다. 같은 테이블을 두 번
 * 읽으면(self join) 둘 다 바뀐다. 직전 식을 찾지 못하면(손으로 다른 경로를 적었다)
 * SQL 을 그대로 두고 `found: false` 를 돌려준다 — 폼이 그 사실을 알린다.
 */
export function replaceSourceRef(
  sql: string,
  prev: SourceRef | undefined,
  next: SourceRef | undefined,
): { sql: string; found: boolean } {
  const from = buildSourceRef(prev);
  if (!sql.includes(from)) return { sql, found: false };
  return { sql: sql.split(from).join(buildSourceRef(next)), found: true };
}

/** 쉼표 구분 입력 → 정규화된 태그 배열. 중복을 제거한다. */
export function parseTags(tags: string | undefined): string[] {
  return [
    ...new Set(
      (tags ?? '')
        .split(',')
        .map(t => t.trim())
        .filter(Boolean),
    ),
  ];
}

/**
 * Airflow 화면 검색용 태그. **팀 약자가 항상 첫 원소**다 — 가이드가
 * "팀 약자는 꼭 넣으세요" 라고 요구하고, 팀별 검색이 그것에 의존한다.
 */
export function buildAirflowTags(opts: {
  teamTag: string;
  extra: string[];
}): string[] {
  return [...new Set([opts.teamTag, ...opts.extra.filter(Boolean)])];
}

/** 카탈로그 엔티티 tags 규칙: lower + `_` → `-`. */
export function normalizeCatalogTags(tags: string[]): string[] {
  return [...new Set(tags.map(t => t.toLowerCase().replace(/_/g, '-')))];
}

/**
 * 로그인 신원의 그룹 → 내가 속한 팀 태그. `TEAM_TAGS` 와 교집합을 낸다.
 *
 * Keycloak 그룹 경로가 `/an`·`/mds`·`/pa` 형태(소문자)이고 팀 폴더가
 * `dags-{group}` 이므로 대문자로 올리면 팀 태그가 된다.
 *
 * **교집합이 필요한 이유:** realm `dataplatform` 은 Airflow·OpenMetadata·AKHQ 가
 * 공유하므로 `om-dpe` 처럼 **DAG 폴더가 없는 그룹**이 섞여 있다(실측 2026-10-27:
 * 17개 그룹). 그걸 그대로 올리면 사용자가 고를 수 있는데 제출은
 * `teamTagSchema`(= `z.enum(TEAM_TAGS)`)가 거부한다 — UI 와 서버가 다른 말을 한다.
 *
 * 한때 "교집합을 내면 Keycloak 에 팀이 추가될 때마다 이 레포 상수를 고쳐야 한다"는
 * 이유로 교집합을 내지 않았는데, 그 목적은 애초에 달성되지 않았다. `teamTagSchema`
 * 가 이미 `TEAM_TAGS` 로 검증하므로 상수는 어차피 유지해야 한다. 그리고 신규 팀은
 * `dp-lakehouse` 에 `dags-{team}/` 루트를 먼저 만들어야 하므로(`types.ts`) 상수
 * 추가는 그 작업과 짝이지 별도 부담이 아니다.
 *
 * ⚠️ **이것은 UX 이고 인가가 아니다.** 드롭다운을 좁혀도 API 는 그대로 열려
 * 있으므로, 서버가 `team_tag ∈ 호출자의 그룹` 을 검사해야 한다(별도 티켓).
 */
export function teamTagsFromOwnership(ownershipEntityRefs: string[]): string[] {
  const allowed = new Set<string>(TEAM_TAGS);
  return groupTagsFromOwnership(ownershipEntityRefs).filter(tag => allowed.has(tag));
}

/**
 * 교집합 **전**의 그룹 태그 — 내가 속한 Keycloak 그룹 전부를 대문자로 올린 것.
 *
 * `teamTagsFromOwnership` 이 비었을 때 **왜 비었는지**를 가르는 데 쓴다. 두 경우는
 * 사용자가 할 일이 달라 보이므로 안내 문구를 나눈다(`PipelineForm`).
 *
 *   그룹 0 개            → 아직 아무 그룹에도 배정되지 않았다
 *   그룹은 있으나 교집합 0 → `om-dpe` 처럼 `dags-{team}/` 폴더가 없는 그룹뿐이다
 */
export function groupTagsFromOwnership(ownershipEntityRefs: string[]): string[] {
  return [
    ...new Set(
      ownershipEntityRefs
        .filter(ref => ref.startsWith('group:'))
        .map(ref => ref.split('/').pop() ?? '')
        .filter(Boolean)
        .map(name => name.toUpperCase()),
    ),
  ].sort();
}
